import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  Button,
  Card,
  Container,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";

import { trackEvent } from "@/tracking";

const FORM_NAME = "contact";

const encode = (data: Record<string, string>) =>
  new URLSearchParams({ "form-name": FORM_NAME, ...data }).toString();

export function ContactForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const form = useForm({
    initialValues: { name: "", email: "", message: "", "bot-field": "" },
    validate: {
      name: (value) =>
        value.trim().length === 0 ? "Please tell us your name" : null,
      email: (value) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? null
          : "Please enter a valid email",
      message: (value) =>
        value.trim().length === 0 ? "Please add a short message" : null,
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setSubmitting(true);
    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode(values),
      });
      if (!response.ok) throw new Error(`Form post failed: ${response.status}`);
      trackEvent("Contact Form Submitted");
      navigate("/thanks");
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Could not send",
        message:
          error instanceof Error
            ? error.message
            : "Please try again in a moment.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container size="sm">
      <Stack gap="lg" align="center" ta="center">
        <Title order={1}>{t("menu.contact")}</Title>
        <Text c="dimmed" maw={520}>
          Drop us a line - we typically reply within a day with availability,
          prices and tips for your stay.
        </Text>
        <Card withBorder w="100%" p={{ base: "md", sm: "xl" }}>
          <form
            name={FORM_NAME}
            method="POST"
            data-netlify="true"
            netlify-honeypot="bot-field"
            onSubmit={form.onSubmit(handleSubmit)}
          >
            <input type="hidden" name="form-name" value={FORM_NAME} />
            <input
              type="hidden"
              name="bot-field"
              {...form.getInputProps("bot-field")}
            />
            <Stack gap="md" ta="left">
              <TextInput
                label="Name"
                placeholder="Your name"
                required
                {...form.getInputProps("name")}
              />
              <TextInput
                label="Email"
                placeholder="you@example.com"
                required
                type="email"
                {...form.getInputProps("email")}
              />
              <Textarea
                label="Message"
                placeholder="When are you thinking of visiting? Any questions?"
                required
                autosize
                minRows={4}
                {...form.getInputProps("message")}
              />
              <Button type="submit" color="sea" loading={submitting} mt="xs">
                Send message
              </Button>
            </Stack>
          </form>
        </Card>
      </Stack>
    </Container>
  );
}

export default ContactForm;
